import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import {
    CdnBaseUrl,
    navigateToExternalLink,
} from '@deltek/specification-client/core/Utilities/urlUtils'
import {
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
} from '@material-ui/core'
import { makeStyles, Theme } from '@material-ui/core/styles'
import CloseIcon from '@material-ui/icons/Close'
import { onEscKeyPressOnDialog } from 'pages/ProjectsPage/WorkspacePage/components/Common/utils'
import React from 'react'
import ImageHoverOverlay from './ImageHoverOverlay'

const CdnClientBaseUrl = `${CdnBaseUrl}/knowledgepoint/client`

const useStyles = makeStyles((theme: Theme) => ({
    dialogPaper: {
        width: pxToRem(600),
        height: pxToRem(700),
        borderRadius: 0,
    },
    dialogTitle: {
        padding: theme.spacing(0),
        height: pxToRem(40),
    },
    dialogContentContainer: {
        paddingBottom: theme.spacing(0),
    },
    dialogContentTitle: {
        fontStyle: 'normal',
        fontWeight: 'normal',
        fontSize: pxToRem(34),
        lineHeight: pxToRem(40),
        display: 'flex',
        alignItems: 'center',
        letterSpacing: theme.spacing(0.03125),
        color: '#304959',
        marginBottom: theme.spacing(1),
    },
    horizontalDivider: {
        width: pxToRem(552),
        color: '#C9C9C9',
        margin: theme.spacing(2.5, 0, 1.5, 0),
    },
    textContent: {
        fontStyle: 'normal',
        fontWeight: 'normal',
        fontSize: pxToRem(14),
        lineHeight: pxToRem(16),
        color: 'rgba(0, 0, 0, 0.54)',
    },
    actionsContainer: {
        height: pxToRem(60),
        margin: theme.spacing(0, 1.25),
        padding: theme.spacing(0, 0, 3.75, 1.25),
        position: 'inherit',
        background: theme.palette.common.white,
    },
    textContainer: {
        alignItems: 'center',
    },
    subTextContainer: {
        textAlign: 'center',
    },
    subTextContent: {
        fontStyle: 'normal',
        fontWeight: 'normal',
        fontSize: pxToRem(13),
        lineHeight: pxToRem(15),
        color: '#A78966',
    },
    imageLinkContainer: {
        marginBottom: theme.spacing(1),
    },
    imageLink: {
        cursor: 'pointer',
        margin: theme.spacing(0.75, 'auto'),
        '&:nth-child(2)': {
            marginLeft: theme.spacing(0),
        },
        '&:nth-child(4)': {
            marginRight: theme.spacing(0),
        },
    },
    checkboxContainer: {
        display: 'flex',
        alignItems: 'center',
        color: 'rgba(0, 0, 0, 0.54)',
    },
    checkbox: {
        color: '#C9C9C9',
        paddingRight: theme.spacing(0.5),
    },
    formControlLabel: {
        cursor: 'pointer',
        fontSize: pxToRem(13),
        letterSpacing: theme.spacing(0.03125),
    },
    goToVideoContainer: {
        textAlign: 'right',
    },
    closeButton: {
        position: 'absolute',
        top: 10,
        right: 10,
    },
    actionButton: {
        background: '#A78966',
        color: theme.palette.common.white,
        fontSize: pxToRem(12),
        fontWeight: 'normal',
        lineHeight: pxToRem(14),
        width: pxToRem(89),
        height: pxToRem(34),
        '&:hover': {
            background: '#A78966',
        },
    },
    doneButton: {
        color: '#A78966',
        fontSize: pxToRem(18),
        fontWeight: 'normal',
        lineHeight: pxToRem(40),
        float: 'right',
    },
}))

const mainURL =
    'https://rise.articulate.com/share/H1ebb9oDYr5EtFgs1zbR2dc1DsXEaebH#/'
const bottomLeftURL =
    'https://education.deltek.com/web/rsl/specificationsolutions/specpoint/lv/ss05006lv_projectworkspaceediting/'
const bottomMiddleURL =
    'https://education.deltek.com/web/rsl/specificationsolutions/specpoint/lv/ss05005lv_buildingaproject/'
const bottomRightURL =
    'https://education.deltek.com/web/rsl/specificationsolutions/specpoint/lv/ss05007lv_searchfilternotes/'
const goToVideosLink =
    'https://education.deltek.com/web/rsl/specificationsolutions/specpoint/portal/specpoint customer assistance program/index.html'

const imageLinks: ImageLink[] = [
    {
        isMain: true,
        src: `${CdnClientBaseUrl}/getting_started_quick_start.jpg`,
        url: mainURL,
        title: 'Quick Start',
    },
    {
        isMain: false,
        src: `${CdnClientBaseUrl}/getting_started_bottom_left.jpg`,
        url: bottomLeftURL,
        title: 'Editing Content',
    },
    {
        isMain: false,
        src: `${CdnClientBaseUrl}/getting_started_bottom_middle.jpg`,
        url: bottomMiddleURL,
        title: 'Building a Project',
    },
    {
        isMain: false,
        src: `${CdnClientBaseUrl}/getting_started_bottom_right.jpg`,
        url: bottomRightURL,
        title: 'Search/Filter/Notes',
    },
]
interface GettingStartedProps {
    isOpen: boolean
    isDontShowAgainChecked: boolean
    handleDialogClose: () => void
    handleDontShowAgain: () => void
}

export interface ImageLink {
    isMain: boolean
    src: string
    url: string
    title: string
}

const GettingStarted: React.FC<GettingStartedProps> = ({
    isOpen,
    isDontShowAgainChecked,
    handleDialogClose,
    handleDontShowAgain,
}) => {
    const classes = useStyles()

    return (
        <Dialog
            open={isOpen}
            BackdropProps={{
                style: { backgroundColor: 'rgba(0,0,0,0.85)' },
            }}
            classes={{ paper: classes.dialogPaper }}
            data-testid="gettingStartedDialog"
            onClose={onEscKeyPressOnDialog(handleDialogClose)}
        >
            <DialogTitle className={classes.dialogTitle}>
                <GridContainer>
                    <IconButton
                        className={classes.closeButton}
                        data-testid="smallScreenWarningCloseButton"
                        onClick={handleDialogClose}
                        disableRipple
                        aria-label="Close Button"
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </GridContainer>
            </DialogTitle>
            <DialogContent className={classes.dialogContentContainer}>
                <GridContainer>
                    <GridItem className={classes.dialogContentTitle}>
                        Getting Started
                    </GridItem>
                    <GridContainer className={classes.imageLinkContainer}>
                        {imageLinks.map((image, index) => {
                            return (
                                <GridItem
                                    className={classes.imageLink}
                                    data-testid="imageLink"
                                    key={index}
                                >
                                    <ImageHoverOverlay {...image} />
                                </GridItem>
                            )
                        })}
                    </GridContainer>
                    <GridContainer className={classes.textContainer}>
                        <GridItem xs={9}>
                            <label className={classes.textContent}>
                                View all tutorial videos, webinars, and FAQs to
                                help you get started.
                            </label>
                        </GridItem>
                        <GridItem xs={3} className={classes.goToVideoContainer}>
                            <Button
                                data-testid="GoVideosButton"
                                className={classes.actionButton}
                                onClick={() =>
                                    navigateToExternalLink(goToVideosLink)
                                }
                                disableRipple
                            >
                                Go to Videos
                            </Button>
                        </GridItem>
                    </GridContainer>

                    <hr className={classes.horizontalDivider} />
                    <GridContainer className={classes.subTextContainer}>
                        <GridItem>
                            <label className={classes.subTextContent}>
                                You can always come back to this screen by
                                hitting the help button top right of the menu.
                            </label>
                        </GridItem>
                    </GridContainer>
                </GridContainer>
            </DialogContent>
            <DialogActions className={classes.actionsContainer}>
                <GridItem xs={6}>
                    <FormControlLabel
                        className={classes.checkboxContainer}
                        control={
                            <Checkbox
                                size="small"
                                data-testid="dontShowAgain"
                                disableRipple
                                className={classes.checkbox}
                                checked={isDontShowAgainChecked}
                                onClick={() => handleDontShowAgain()}
                            />
                        }
                        label={
                            <label
                                className={classes.formControlLabel}
                                onClick={() => handleDontShowAgain()}
                            >
                                Don't show again
                            </label>
                        }
                    />
                </GridItem>
                <GridItem xs={6}>
                    <Button
                        className={classes.doneButton}
                        onClick={handleDialogClose}
                        data-testid="doneButton"
                        disableRipple
                    >
                        Done
                    </Button>
                </GridItem>
            </DialogActions>
        </Dialog>
    )
}

export default GettingStarted
